import { useCallback } from 'react';

import { PatientGroup } from '@dreryk/egabinet-patient-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { Form, FormRouteLeavingPrompt, StyledFlex } from '@dreryk/react-components';

import { PatientGroupsAssignmentsMultiselect } from './fields/PatientGroupsAssignmentsMultiselect';
import {
   PatientGroupsAssignmentsFormBean,
   PatientGroupsAssignmentsFormController,
   PatientGroupsAssignmentsFormInitializer,
} from './PatientGroupsAssignmentsForm.types';
import { PatientGroupsAssignmentsSelectedGroupView } from './PatientGroupsAssignmentsSelectedGroupView';

type PatientGroupFormRenderProps = Pick<PatientGroupsAssignmentsFormController, 'handleSubmit' | 'markPristine'>;

type PatientGroupsAssignmentsFormProps = Readonly<{
   patientGroups: PatientGroup[];
   initializer: PatientGroupsAssignmentsFormInitializer;
   children: (renderProps: PatientGroupFormRenderProps) => JSX.Element;
}>;

export const PatientGroupsAssignmentsForm = (props: PatientGroupsAssignmentsFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      markPristine,
      formState,
      watch,
      setValue,
   } = useFormController<PatientGroupsAssignmentsFormBean>(props.initializer());
   const selectedGroups = watch('patientGroups');
   const handleClear = useCallback(
      (group: PatientGroup) => {
         setValue(
            'patientGroups',
            selectedGroups.filter((selectedGroup) => selectedGroup.id !== group.id),
            { shouldDirty: true }
         );
      },
      [selectedGroups, setValue]
   );

   return (
      <>
         <Form autoComplete={false} noValidate>
            <StyledFlex gap={16} alignItems={'flex-start'}>
               <StyledFlex flex={'none'}>
                  <PatientGroupsAssignmentsMultiselect register={register} patientGroups={props.patientGroups} />
               </StyledFlex>
               <PatientGroupsAssignmentsSelectedGroupView selectedGroups={selectedGroups} onClear={handleClear} />
            </StyledFlex>
         </Form>
         <FormRouteLeavingPrompt formState={formState} />

         {props.children({ handleSubmit: handleSubmit, markPristine: markPristine })}
      </>
   );
};
